import { SLOTS, type Slot } from '../net/protocol';

/**
 * The auto-bet scheduler.
 *
 * It repeats an ordinary `bet.place` once per round. There is no subscription on
 * the server and this does not add one, which is a deliberate choice rather than
 * a shortcut: a server-side "keep betting for me" would spend a player's balance
 * while they are not connected, and unattended play is a responsible-gambling
 * question to answer on its own terms, not a side effect of a UI switch.
 *
 * Because every repetition is a normal stake, the server re-checks the balance,
 * the operator limits, the player's own limits and their right to bet at all on
 * each one -- exactly as it does for a press of the button.
 *
 * What this class is really about is the stopping rules. An automation that
 * keeps trying after a refusal produces one error toast per round for as long as
 * the player is away, so:
 *
 *   - any refusal that will refuse again turns the switch off, once, with a
 *     notice;
 *   - a dropped connection pauses instead of stopping, because the socket's own
 *     reconnect brings a snapshot and that is when state is known again;
 *   - cancelling a stake by hand turns the switch off, because "I cancelled it
 *     and it bet again anyway" is a way to lose money by surprise.
 */

/** Refusals that would refuse again next round; each one stops the automation. */
const TERMINAL_CODES = new Set([
  'insufficient_funds',
  'stake_out_of_range',
  'limit_exceeded',
  'betting_blocked',
  'invalid_auto_cashout',
  'invalid_slot',
  'unauthorized',
]);

export interface AutoBetHost {
  /**
   * Places this slot's stake. Returns false when the panel would not have sent
   * anything anyway (no valid stake, already staked, a request in flight).
   */
  place: (slot: Slot, nextRound: boolean) => boolean;
  /** Puts the slot's switch back to off, without calling back into here. */
  turnOff: (slot: Slot) => void;
  /** Tells the player the automation stopped, once. */
  notifyStopped: () => void;
}

export class AutoBet {
  private readonly enabled = new Set<Slot>();

  /**
   * True from the moment the socket drops until a snapshot has been applied.
   *
   * Betting while paused would race the reconnect: the server may already hold
   * the stake this client is about to place again, and the answer to that is
   * `already_bet` -- an error the player did nothing to cause.
   */
  private paused = false;

  constructor(private readonly host: AutoBetHost) {}

  isOn(slot: Slot): boolean {
    return this.enabled.has(slot);
  }

  /**
   * Turns the automation on or off for one slot.
   *
   * `placeNow` asks for an immediate pre-bet, which is what "on" means while a
   * round is already climbing: the next stake this switch is responsible for
   * belongs to the round after the one on screen, and the server already accepts
   * exactly that.
   */
  set(slot: Slot, on: boolean, opts: { placeNow?: boolean } = {}): void {
    if (on) {
      this.enabled.add(slot);
      if (opts.placeNow && !this.paused) this.host.place(slot, true);
    } else {
      this.enabled.delete(slot);
    }
  }

  /** Called when the server opens a betting window. */
  betsOpened(): void {
    if (this.paused) return;
    for (const slot of SLOTS) {
      if (this.enabled.has(slot)) this.host.place(slot, false);
    }
  }

  /** Called on every socket status change. */
  setPaused(paused: boolean): void {
    this.paused = paused;
  }

  /**
   * Called for every error the server sends.
   *
   * An error with no slot stops every enabled slot: `unauthorized` and
   * `betting_blocked` belong to the account, not to one panel.
   */
  onError(slot: Slot | null, code: string): void {
    if (!TERMINAL_CODES.has(code)) return;

    const affected = slot === null ? [...this.enabled] : this.enabled.has(slot) ? [slot] : [];
    if (affected.length === 0) return;

    for (const s of affected) {
      this.enabled.delete(s);
      this.host.turnOff(s);
    }
    // One notice however many slots stopped: the refusal itself is already being
    // shown as its own toast.
    this.host.notifyStopped();
  }

  /** Called when the player cancels a stake themselves. */
  onManualCancel(slot: Slot): void {
    if (!this.enabled.delete(slot)) return;
    this.host.turnOff(slot);
    this.host.notifyStopped();
  }

  /** Called when the account is blocked mid-session. */
  stopAll(): void {
    if (this.enabled.size === 0) return;
    for (const slot of [...this.enabled]) {
      this.enabled.delete(slot);
      this.host.turnOff(slot);
    }
    this.host.notifyStopped();
  }
}
